/**
 * Pure H · empty-state atom.
 *
 * Every list, chart, KPI-aggregate, and scorecard MUST bail out through
 * this component when its source array is empty or undefined — instead
 * of crashing the ErrorBoundary with a phantom `undefined.foo` access.
 *
 * Keep it visually calm; the presence of an empty-state should look
 * intentional, not like a broken page.
 */
import type { ReactNode } from "react";

export function EmptyState({
  testId,
  title,
  hint,
  action,
  compact = false,
}: {
  testId?: string;
  title: string;
  hint?: string;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      data-testid={testId || "empty-state"}
      className={compact ? "p-3" : "py-8 px-4"}
      style={{
        color: "var(--ph-ink-muted, #64748b)",
        textAlign: "center",
      }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.16em] mb-1"
        style={{ color: "var(--ph-ink-soft, #94a3b8)" }}
      >
        No data
      </div>
      <div
        className={compact ? "text-xs" : "text-sm"}
        style={{ color: "var(--ph-ink-muted)" }}
      >
        {title}
      </div>
      {hint && (
        <div
          className="mt-1 text-xs"
          style={{ color: "var(--ph-ink-soft)" }}
        >
          {hint}
        </div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** Safe last-of-array. */
export function last<T>(arr: T[] | undefined | null): T | undefined {
  if (!arr || arr.length === 0) return undefined;
  return arr[arr.length - 1];
}
